import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AIResearchWorkspace from "./AIResearchWorkspace";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

// FC259 is a read-only listing viewer. Never infer print/finish identity from titles or SKUs.
const displayValue = (value, fallback = "Not recorded") =>
  value === null || value === undefined || String(value).trim() === ""
    ? fallback
    : String(value).trim();

const listingId = (listing) =>
  listing?.id === null || listing?.id === undefined ? null : String(listing.id);

const aud = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "Not recorded";
  }
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString("en-AU", { style: "currency", currency: "AUD" })
    : "Invalid stored price";
};

const quantity = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "Not recorded";
  }
  const count = Number(value);
  return Number.isFinite(count) ? String(count) : "Invalid stored quantity";
};

const verifiedFinish = (value) => {
  const finish = displayValue(value, "");
  return finish && !["unknown", "null", "none"].includes(finish.toLowerCase())
    ? finish
    : "Unknown / needs verification";
};

const lastSync = (value) => {
  const timestamp = displayValue(value, "");
  return timestamp
    ? `${timestamp} (as stored; timezone not verified)`
    : "Not recorded — freshness unknown";
};

function Detail({ label, value }) {
  return (
    <Box sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function AIOperator() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("Listed");
  const [selectedId, setSelectedId] = useState(null);
  const [workspaceId, setWorkspaceId] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");

    // This is the only network operation in FC259: existing read-only GET.
    axios.get("/api/listings", { signal: controller.signal })
      .then(({ data }) => {
        if (!active) return;
        if (!Array.isArray(data)) throw new Error("Listings returned an unexpected response.");
        setListings(data);
        setSelectedId((current) =>
          current !== null && data.some((item) => listingId(item) === current)
            ? current
            : null
        );
      })
      .catch((err) => {
        if (!active || axios.isCancel(err)) return;
        setListings([]);
        setSelectedId(null);
        setError(err?.response?.data?.error || err?.message || "Could not load listings.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [reload]);

  const statuses = useMemo(() => {
    const values = new Map();
    listings.forEach((item) => {
      const value = displayValue(item?.listing_status, "Unknown");
      values.set(value.toLowerCase(), value);
    });
    values.set("listed", "Listed");
    return [...values.values()].sort((a, b) => a.localeCompare(b));
  }, [listings]);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return listings.filter((item) => {
      const localStatus = displayValue(item?.listing_status, "Unknown");
      if (status !== "All" && localStatus.toLowerCase() !== status.toLowerCase()) return false;
      if (!term) return true;
      return [item?.id, item?.card_name, item?.listing_title, item?.sku, item?.card_set]
        .some((value) => value !== null && value !== undefined && String(value).toLowerCase().includes(term));
    });
  }, [listings, query, status]);

  const selected = visible.find((item) => listingId(item) === selectedId) || null;

  if (workspaceId !== null) {
    return <AIResearchWorkspace
      listingId={workspaceId}
      listing={listings.find((item) => listingId(item) === workspaceId) || null}
      onBack={() => setWorkspaceId(null)}
      reloadListings={() => setReload((prev) => prev + 1)}
      listingsLoading={loading}
      listingsError={error}
    />;
  }

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            AI Operator — Playable Card Research
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select and inspect an existing listing. This is not a verified catalogue of playable cards.
          </Typography>
        </Box>
        <Chip label="Research • No eBay mutations" variant="outlined" color="primary" sx={{ alignSelf: "flex-start", fontWeight: 600 }} />
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Prices, available quantities and eBay statuses below are <strong>app-stored snapshots</strong>, not a live eBay check. Viewing and refreshing this page never updates listings or contacts eBay.
      </Alert>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: "center" }}>
        <TextField
          label="Search listings"
          size="small"
          placeholder="Card, title, set, SKU or local listing ID"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSelectedId(null); }}
          inputProps={{ "aria-label": "Search listings" }}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <FormControl size="small" sx={{ minWidth: { md: 185 } }}>
          <InputLabel id="ai-operator-local-status-label">Local status</InputLabel>
          <Select
            labelId="ai-operator-local-status-label"
            value={status}
            label="Local status"
            onChange={(e) => { setStatus(e.target.value); setSelectedId(null); }}
          >
            <MenuItem value="All">All statuses</MenuItem>
            {statuses.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={() => setReload((prev) => prev + 1)} disabled={loading} sx={{ whiteSpace: "nowrap" }}>
          Refresh stored listings
        </Button>
      </Stack>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} role="status">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading stored listings…</Typography>
        </Stack>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>Couldn't load listings: {error}</Alert>}

      {!loading && !error && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", xl: "minmax(0, 1.8fr) minmax(330px, 1fr)" }, gap: 2 }}>
          <Paper variant="outlined" sx={{ minWidth: 0, overflow: "hidden" }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Listings ({visible.length} matching)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Default: local Listed records. Other statuses can be inspected using the filter.
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 540 }}>
              <Table size="small" stickyHeader sx={{ minWidth: 960 }} aria-label="Read-only listings for AI Operator selection">
                <TableHead>
                  <TableRow>
                    <TableCell>Local ID</TableCell>
                    <TableCell>Card / listing title</TableCell>
                    <TableCell>Set</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Local status</TableCell>
                    <TableCell>eBay status</TableCell>
                    <TableCell align="right">Stored price</TableCell>
                    <TableCell align="right">Stored eBay qty</TableCell>
                    <TableCell align="right">Inspect</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((item, index) => {
                    const id = listingId(item);
                    const isSelected = id !== null && selectedId === id;
                    const name = displayValue(item.card_name, "Unnamed card");
                    const title = displayValue(item.listing_title, "");
                    return (
                      <TableRow key={id ?? `missing-id-${index}`} selected={isSelected} hover>
                        <TableCell sx={{ fontWeight: isSelected ? 700 : 500 }}>{id ?? "Missing ID"}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
                          {title && title !== name && <Typography variant="caption" color="text.secondary">{title}</Typography>}
                        </TableCell>
                        <TableCell>{displayValue(item.card_set)}</TableCell>
                        <TableCell>{displayValue(item.sku)}</TableCell>
                        <TableCell>{displayValue(item.listing_status)}</TableCell>
                        <TableCell>{displayValue(item.ebay_listing_status)}</TableCell>
                        <TableCell align="right">{aud(item.listing_price)}</TableCell>
                        <TableCell align="right">{quantity(item.ebay_available_quantity)}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant={isSelected ? "contained" : "outlined"}
                            disabled={id === null}
                            onClick={() => setSelectedId(id)}
                            aria-label={`View local listing ${id ?? "without ID"}`}
                          >
                            {isSelected ? "Selected" : "View"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {visible.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                        No matching stored listings. Try another search or choose All statuses.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, minWidth: 0, alignSelf: "start" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Selected listing — read-only details
            </Typography>
            {!selected ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                Select View beside a listing to inspect its stored identifiers and values.
              </Typography>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  This is local listing #{listingId(selected)}, not an identity-verified playable-card record.
                </Alert>
                <Detail label="Local listing ID (canonical)" value={listingId(selected)} />
                <Detail label="Card name" value={displayValue(selected.card_name)} />
                <Detail label="Listing title" value={displayValue(selected.listing_title)} />
                <Detail label="Set" value={displayValue(selected.card_set)} />
                <Detail label="SKU" value={displayValue(selected.sku)} />
                <Detail label="eBay listing ID" value={displayValue(selected.ebay_listing_id)} />
                <Detail label="eBay offer ID" value={displayValue(selected.ebay_offer_id)} />
                <Detail label="Condition (stored)" value={displayValue(selected.listing_condition)} />
                <Detail label="Verified finish" value={verifiedFinish(selected.fc256_verified_finish)} />
                <Detail label="Price (AUD, app-stored)" value={aud(selected.listing_price)} />
                <Detail label="eBay available quantity (app-stored)" value={quantity(selected.ebay_available_quantity)} />
                <Detail label="Local listing status" value={displayValue(selected.listing_status)} />
                <Detail label="eBay listing status (app-stored)" value={displayValue(selected.ebay_listing_status)} />
                <Detail label="Last eBay sync (app-stored)" value={lastSync(selected.last_ebay_sync_at)} />
                <Button
                  variant="contained"
                  sx={{ mt: 2, width: "100%" }}
                  onClick={() => setWorkspaceId(listingId(selected))}
                >
                  Open Research Workspace
                </Button>
              </>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
}
